"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const redux_1 = require("redux");
const getCampaignType_1 = require("../views/utils/getCampaignType");
const formatDateTime_1 = require("../views/utils/formatDateTime");
const experiences_1 = require("./experiences");
const luxon_1 = require("luxon");
const campaigns = (state = {}, action) => {
    if (action.campaigns) {
        return Object.assign({}, action.campaigns);
    }
    if (action.campaignConnection) {
        return Object.assign({}, action.campaignConnection.campaigns);
    }
    return state;
};
const pageInfo = (state = {}, action) => {
    if (action.campaignConnection) {
        return action.campaignConnection.pageInfo;
    }
    return state;
};
exports.default = redux_1.combineReducers({
    campaigns,
    pageInfo,
});
exports.getCampaign = (state, id) => {
    return state.campaigns[id] || null;
};
exports.getAllCampaigns = (state) => {
    if (state && state.campaigns) {
        return Object.keys(state.campaigns).map((id) => exports.getCampaign(state, id));
    }
    return [];
};
exports.getFilteredCampaigns = (state, filter) => {
    const allCampaigns = exports.getAllCampaigns(state);
    switch (filter) {
        case 'drafts':
            return allCampaigns.filter((e) => e.status === 'DRAFT');
        case 'published':
            return allCampaigns.filter((e) => e.status === 'PUBLISHED');
        case 'archived':
            return allCampaigns.filter((e) => e.status === 'ARCHIVED');
        case 'all':
            return allCampaigns.filter((e) => e.status !== 'ARCHIVED');
        default:
            throw new Error(`Unknown filter: ${filter}`);
    }
};
exports.getCampaignsByTriggerType = (campaigns, triggerType) => {
    if (triggerType === 'all') {
        return campaigns;
    }
    return campaigns.filter((c) => c.trigger.__typename === triggerType);
};
exports.getOrderedCampaignList = (state, filter, type) => {
    let triggerType;
    switch (type) {
        case 'api':
            triggerType = 'CampaignAPITrigger';
            break;
        case 'automated':
            triggerType = 'CampaignAutomatedTrigger';
            break;
        case 'scheduled':
            triggerType = 'CampaignScheduledTrigger';
            break;
        case 'web':
            triggerType = 'URLCampaignTrigger';
            break;
        case 'all':
        default:
            triggerType = 'all';
            break;
    }
    return exports.getCampaignsByTriggerType(exports.getFilteredCampaigns(state, filter), triggerType).reverse();
};
const getHourMinuteSecond = (totalSeconds) => {
    const hour = Math.floor(totalSeconds / 3600);
    const minute = Math.floor((totalSeconds - hour * 3600) / 60);
    const second = totalSeconds - hour * 3600 - minute * 60;
    return {
        hour,
        minute,
        second,
    };
};
const getYearMonthDay = (date) => {
    const [year, month, day] = date.split('-').map((d) => parseInt(d, 10));
    return {
        year,
        month,
        day,
    };
};
exports.getIsCampaignFinished = (state, campaignId) => {
    const { status, trigger } = exports.getCampaign(state, campaignId);
    if (getCampaignType_1.isAPICampaignTrigger(trigger)) {
        return status === 'ARCHIVED';
    }
    if (getCampaignType_1.isScheduledCampaignTrigger(trigger)) {
        if (!trigger.dateTime) {
            return false;
        }
        const { date, time, timeZone } = trigger.dateTime;
        return (luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(date)), getHourMinuteSecond(time)), { zone: timeZone || 'UTC-12' })) < luxon_1.DateTime.local());
    }
    if (getCampaignType_1.isAutomatedCampaignTrigger(trigger)) {
        const s = exports.getEventTriggerFilterByType(trigger.eventTrigger.filters, 'ScheduledEventTriggerFilter');
        if (s && getCampaignType_1.isScheduledEventTriggerFilter(s)) {
            const { endDateTime } = s;
            if (!endDateTime) {
                return false;
            }
            const { date, time, timeZone } = endDateTime;
            return (luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(date)), getHourMinuteSecond(time)), { zone: timeZone || 'UTC-12' })) < luxon_1.DateTime.local());
        }
        return false;
    }
    return false;
};
exports.getDisplayTime = (campaign) => {
    const { trigger } = campaign;
    if (getCampaignType_1.isAutomatedCampaignTrigger(trigger)) {
        const t = exports.getEventTriggerFilterByType(trigger.eventTrigger.filters, 'TimeOfDayEventTriggerFilter');
        if (t && getCampaignType_1.isTimeOfDayEventTriggerFilter(t)) {
            return formatDateTime_1.formatDisplayTime(t.startTime);
        }
    }
    if (getCampaignType_1.isScheduledCampaignTrigger(trigger)) {
        return trigger.dateTime ? formatDateTime_1.formatDisplayTime(trigger.dateTime.time) : '';
    }
};
exports.getScheduledSentAtTime = (campaign) => {
    const { trigger } = campaign;
    if (getCampaignType_1.isScheduledCampaignTrigger(trigger)) {
        if (trigger.dateTime) {
            if (!trigger.dateTime.timeZone) {
                return formatDateTime_1.formatDisplayTime(trigger.dateTime.time);
            }
            return luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(trigger.dateTime.date)), getHourMinuteSecond(trigger.dateTime.time)), { zone: trigger.dateTime.timeZone }))
                .toLocal()
                .toLocaleString(luxon_1.DateTime.TIME_SIMPLE);
        }
    }
    return '';
};
exports.getFormatDate = (campaign, automatedEndDate) => {
    const { trigger } = campaign;
    if (getCampaignType_1.isScheduledCampaignTrigger(trigger)) {
        return trigger.dateTime ? formatDateTime_1.formatDate(trigger.dateTime.date) : new Date();
    }
    if (getCampaignType_1.isAutomatedCampaignTrigger(trigger)) {
        const s = exports.getEventTriggerFilterByType(trigger.eventTrigger.filters, 'ScheduledEventTriggerFilter');
        if (s && getCampaignType_1.isScheduledEventTriggerFilter(s)) {
            if (s.endDateTime && automatedEndDate) {
                return formatDateTime_1.formatDate(s.endDateTime.date);
            }
            else if (!automatedEndDate && s.startDateTime) {
                return formatDateTime_1.formatDate(s.startDateTime.date);
            }
        }
    }
};
exports.getDuplicateCampaignName = (campaign) => {
    const { name } = campaign;
    const duplicatedCampaignRegEx = /copy\s?\d*$/;
    if (!duplicatedCampaignRegEx.exec(name)) {
        return `${name} copy`;
    }
    else {
        const res = /\d*$/.exec(name);
        const copyNumber = res != null ? res[0] : false;
        if (!copyNumber) {
            return `${name} 2`;
        }
        else {
            const newCopyNumber = (parseInt(copyNumber, 10) + 1).toString();
            return name.replace(/\d*$/, newCopyNumber);
        }
    }
};
exports.getCampaignExists = (state, id) => exports.getCampaign(state, id) !== null;
exports.getExperiencePreviewURL = (state, id) => {
    const tapBehavior = exports.getCampaignTapBehavior(state.campaignConnection, id);
    if (tapBehavior.type === 'PRESENT_EXPERIENCE' && !!tapBehavior.experienceID) {
        const experience = experiences_1.getExperience(state.experiences, tapBehavior.experienceID);
        if (experience) {
            return experience.simulatorURL;
        }
    }
    return '';
};
exports.getAutomatedActiveStatus = (state, id) => {
    const { trigger } = exports.getCampaign(state, id);
    if (getCampaignType_1.isAutomatedCampaignTrigger(trigger)) {
        const scheduledEventTriggerFilter = exports.getEventTriggerFilterByType(trigger.eventTrigger.filters, 'ScheduledEventTriggerFilter');
        if (scheduledEventTriggerFilter) {
            const { startDateTime, endDateTime } = scheduledEventTriggerFilter;
            if (startDateTime == null) {
                return 'UNKNOWN';
            }
            const now = luxon_1.DateTime.local();
            if (now <
                luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(startDateTime.date)), getHourMinuteSecond(startDateTime.time)), { zone: startDateTime.timeZone ? startDateTime.timeZone : 'UTC+14' }))) {
                return 'UPCOMING';
            }
            else if (endDateTime &&
                now >
                    luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(endDateTime.date)), getHourMinuteSecond(endDateTime.time)), { zone: endDateTime.timeZone ? endDateTime.timeZone : 'UTC-12' }))) {
                return 'EXPIRED';
            }
            else {
                return 'ACTIVE';
            }
        }
    }
    return 'UNKNOWN';
};
exports.getEventTriggerFilterByType = (filters, type) => filters.find((f) => f.__typename === type);
exports.getAPICampaignTrigger = (state, id) => {
    const { trigger } = exports.getCampaign(state, id);
    if (getCampaignType_1.isAPICampaignTrigger(trigger)) {
        return trigger;
    }
};
exports.getAutomatedCampaignTrigger = (state, id) => {
    const { trigger } = exports.getCampaign(state, id);
    if (getCampaignType_1.isAutomatedCampaignTrigger(trigger)) {
        return trigger;
    }
};
exports.getScheduledCampaignTrigger = (state, id) => {
    const { trigger } = exports.getCampaign(state, id);
    if (getCampaignType_1.isScheduledCampaignTrigger(trigger)) {
        return trigger;
    }
};
exports.getPublishedCampaignLifecycleStatus = (state, id) => {
    const { status } = exports.getCampaign(state, id);
    if (status !== 'PUBLISHED') {
        return 'UNKNOWN';
    }
    const APICampaignTrigger = exports.getAPICampaignTrigger(state, id);
    const AutomatedCampaignTrigger = exports.getAutomatedCampaignTrigger(state, id);
    const ScheduledCampaignTrigger = exports.getScheduledCampaignTrigger(state, id);
    if (APICampaignTrigger) {
        return 'ACTIVE';
    }
    if (AutomatedCampaignTrigger) {
        return exports.getAutomatedActiveStatus(state, id);
    }
    if (ScheduledCampaignTrigger) {
        const { dateTime: { date, time, timeZone }, } = ScheduledCampaignTrigger;
        const now = luxon_1.DateTime.local();
        return now >
            luxon_1.DateTime.fromObject(Object.assign(Object.assign(Object.assign({}, getYearMonthDay(date)), getHourMinuteSecond(time)), { zone: timeZone ? timeZone : undefined }))
            ? 'SENT'
            : 'SCHEDULED';
    }
    return 'UNKNOWN';
};
exports.getCampaignTapBehavior = (state, id) => {
    const { tapBehavior } = exports.getCampaign(state, id).deliverable;
    return tapBehavior;
};
exports.getCampaignTimeZone = (state, id) => {
    const AutomatedCampaignTrigger = exports.getAutomatedCampaignTrigger(state, id);
    const ScheduledCampaignTrigger = exports.getScheduledCampaignTrigger(state, id);
    if (AutomatedCampaignTrigger) {
        const scheduledEventTriggerFilter = exports.getEventTriggerFilterByType(AutomatedCampaignTrigger.eventTrigger.filters, 'ScheduledEventTriggerFilter');
        if (scheduledEventTriggerFilter) {
            const { startDateTime } = scheduledEventTriggerFilter;
            if (startDateTime && startDateTime.timeZone) {
                return startDateTime.timeZone;
            }
        }
    }
    if (ScheduledCampaignTrigger) {
        const { dateTime } = ScheduledCampaignTrigger;
        return dateTime ? dateTime.timeZone : null;
    }
    return null;
};
exports.getCampaignHasNextPage = (state) => {
    return state.pageInfo.hasNextPage ? state.pageInfo.hasNextPage : false;
};
exports.getCampaignHasPreviousPage = (state) => {
    return state.pageInfo.hasPreviousPage ? state.pageInfo.hasPreviousPage : false;
};
exports.getCampaignStartCursor = (state) => {
    return state.pageInfo.startCursor;
};
exports.getCampaignEndCursor = (state) => {
    return state.pageInfo.endCursor;
};
